import {otsBaseApi} from "../../ots-api";
import {Dispatch, DispatchPatchDTO, DispatchSearchCriteria} from "../../../model/dispatch";
import {driverActivityApi, driverActivityCacheUpdate} from "../driver-activity/driver-activity-store";
import {
  DispatchNotificationBatchCreateRequest,
  DispatchNotificationEntry,
  DispatchNotificationBatchCreateResponse,
  DispatchNotificationCreateRequest,
  DispatchNotificationCreateResponse,
  DispatchNotificationPreviewRequest,
  DispatchNotificationPreviewResponse
} from "../../../model/dispatch-notification";
import {DispatchTrackingResult} from "../../../model/dispatch-tracking";
import {DispatchCarryover, DispatchCarryoverPolicy, DispatchCarryoverPolicyArgs} from "../../../model/dispatch-carryover";

const ALL_TAG_TYPES = [
  "Dispatches",
  "DispatchTtas",
  "DispatchSplits",
  "DriverActivity",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const applyDispatchPatch = (dispatch: Dispatch | undefined, patch: DispatchPatchDTO): void => {
  if (!dispatch) {
    return;
  }

  for (const [key, patchValue] of Object.entries(patch)) {
    if (patchValue && "present" in patchValue && patchValue.present) {
      (dispatch as any)[key] = patchValue.value;
    }
  }
}

export const dispatchApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDispatch: builder.query<Dispatch, string>({
      query: (id) => ({
        url: `/dispatch/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Dispatches", id}]
    }),
    getDispatchTracking: builder.query<DispatchTrackingResult, string>({
      query: (id) => ({
        url: `/dispatch/${id}/tracking`,
        method: "GET",
        params: {
          timeFormat: "epoch"
        }
      }),
      providesTags: (result, error, id) => [{type: "Dispatches", id}]
    }),
    getDispatchCarryoverPolicy: builder.query<DispatchCarryoverPolicy, DispatchCarryoverPolicyArgs>({
      query: (params) => ({
        url: `/dispatch/carryover-policy`,
        method: "GET",
        params,
      }),
    }),
    getDispatchNotifications: builder.query<DispatchNotificationEntry[], string>({
      query: (id) => ({
        url: `/dispatch/${id}/notifications`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "Dispatches", id}]
    }),
    getDispatchCarryovers: builder.query<DispatchCarryover[], string>({
      query: (id) => ({
        url: `/dispatch/${id}/carryovers`,
        method: "GET",
      }),
      // refetches whenever the dispatch itself is invalidated
      providesTags: (result, error, id) => [{type: "Dispatches", id}]
    }),
    searchDispatches: builder.query<
      Dispatch[],
      DispatchSearchCriteria
    >({
      query: (criteria) => ({
        url: "/dispatch/search",
        method: "POST",
        body: criteria
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Dispatches", id} as const)
            ),
            {type: "Dispatches", id: "LIST"}
          ];
        } else {
          return [{type: "Dispatches", id: "LIST"}];
        }
      }
    }),
    getSplitDispatchesByDispatch: builder.query<Dispatch[], string>({
      query: (arg) => ({
        url: `/dispatch/all/splits/${arg}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Dispatches", id} as const)
            ),
            {type: "DispatchSplits", id: arg}
          ];
        } else {
          return [{type: "DispatchSplits", id: arg}];
        }
      }
    }),
    getDispatchesByDay: builder.query<
      Dispatch[],
      {
        day: string,
        regionId: string
      }
    >({
      query: (arg) => ({
        url: `/dispatch?day=${arg.day}&regionId=${arg.regionId}`,
        method: "GET",
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Dispatches", id} as const)
            ),
            {type: "Dispatches", id: "LIST"}
          ];
        } else {
          return [{type: "Dispatches", id: "LIST"}];
        }
      }
    }),
    createDispatch: builder.mutation<Dispatch, Partial<Dispatch>>({
      query: (arg) => ({
        url: "/dispatch",
        method: "POST",
        body: arg
      }),
      invalidatesTags: [
        {type: "Dispatches", id: "LIST"},
        {type: "DriverActivity"},
      ]
    }),
    updateDispatch: builder.mutation<boolean, Partial<Dispatch>>({
      query: (arg) => ({
        url: "/dispatch",
        method: "PUT",
        body: arg,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Dispatches", id},
      ],
      async onQueryStarted(arg, {dispatch, queryFulfilled, getState}) {
        try {
          const {data} = await queryFulfilled
          if (data && arg.id) {
            const {data: otsDispatch} = await dispatch(dispatchApi.endpoints.getDispatch.initiate(arg.id))
            if (otsDispatch) {
              driverActivityCacheUpdate(dispatch, getState(), (draft) => {
                const index = draft.findIndex(s => s.dispatch?.id === otsDispatch.id)
                if (index >= 0) {
                  draft[index] = otsDispatch
                }
              })
            }
          }
        } catch (error) {
          console.debug(error)
        }

      },
    }),
    patchDispatch: builder.mutation<boolean, { id: string, patch: DispatchPatchDTO }>({
      query: (arg) => ({
        url: `/dispatch/${arg.id}`,
        method: "PATCH",
        body: arg.patch,
      }),
      async onQueryStarted(arg, {dispatch, queryFulfilled, getState}) {
        const patchResults: { undo: () => void }[] = [];
        const queries = dispatchApi.util.selectInvalidatedBy(getState(), [{type: "Dispatches", id: arg.id}])

        for (const query of queries) {
          const {endpointName, originalArgs} = query
          if (endpointName === 'getDispatch') {
            patchResults.push(dispatch(
              dispatchApi.util.updateQueryData('getDispatch', originalArgs, (draft) => {
                applyDispatchPatch(draft, arg.patch);
              })
            ))
          } else if (endpointName === 'searchDispatches') {
            patchResults.push(dispatch(
              dispatchApi.util.updateQueryData('searchDispatches', originalArgs, (draft) => {
                const index = draft.findIndex(d => d.id === arg.id);
                applyDispatchPatch(draft[index], arg.patch);
              })
            ))
          } else if (endpointName === 'getDispatchesByDay') {
            patchResults.push(dispatch(
              dispatchApi.util.updateQueryData('getDispatchesByDay', originalArgs, (draft) => {
                const index = draft.findIndex(d => d.id === arg.id);
                applyDispatchPatch(draft[index], arg.patch);
              })
            ))
          }
        }

        const driverActivityQueries = driverActivityApi.util.selectInvalidatedBy(getState(), [{type: "DriverActivity"}])
        for (const query of driverActivityQueries) {
          const {endpointName, originalArgs} = query
          if (endpointName === 'searchDriverActivity') {
            patchResults.push(dispatch(
              driverActivityApi.util.updateQueryData('searchDriverActivity', originalArgs, (draft) => {
                const index = draft.findIndex(s => s.dispatch?.id === arg.id)
                if (index >= 0) {
                  applyDispatchPatch(draft[index].dispatch, arg.patch);
                }
              })
            ))
          } else if (endpointName === 'getDriverActivityByDay') {
            patchResults.push(dispatch(
              driverActivityApi.util.updateQueryData('getDriverActivityByDay', originalArgs, (draft) => {
                const index = draft.findIndex(s => s.dispatch?.id === arg.id)
                if (index >= 0) {
                  applyDispatchPatch(draft[index].dispatch, arg.patch);
                }
              })
            ))
          }
        }

        try {
          await queryFulfilled
        } catch (error) {
          patchResults.forEach((patchResult) => patchResult.undo())
          dispatch(dispatchApi.util.invalidateTags([{type: "Dispatches", id: arg.id}]))
          dispatch(driverActivityApi.util.invalidateTags([{type: "DriverActivity"}]))
          console.debug(error)
        }
      },
    }),
    updateDispatches: builder.mutation<Dispatch[], Partial<Dispatch>[]>({
      query: (arg) => ({
        url: "/dispatch/all",
        method: "PUT",
        body: arg,
      }),
      invalidatesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Dispatches", id} as const)
            ),
          ];
        } else {
          return [];
        }
      },
      async onQueryStarted(arg, {dispatch, queryFulfilled, getState}) {
        try {
          const {data: dataArray} = await queryFulfilled
          for (const data of dataArray) {
            driverActivityCacheUpdate(dispatch, getState(), (draft) => {
              const index = draft.findIndex(s => s.dispatch?.id === data.id)
              if (index >= 0) {
                draft[index] = data
              }
            })
          }
        } catch (error) {
          console.debug(error)
        }

      },
    }),
    deleteDispatch: builder.mutation<void, Partial<Dispatch>>({
      query: (arg) => ({
        url: "/dispatch",
        method: "DELETE",
        body: arg
      }),
      invalidatesTags: (result, error, {id}) => [{
        type: "Dispatches", id
      }],
      async onQueryStarted({id}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            const index = draft.findIndex(s => s.dispatch?.id === id)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    createDispatchNotification: builder.mutation<DispatchNotificationCreateResponse, {
      id: string,
      req: DispatchNotificationCreateRequest
    }>({
      query: ({id, req}) => ({
        url: `/dispatch/${id}/notifications`,
        method: "POST",
        body: req
      }),
      // refreshes the notified bell and the dispatch's notification history
      invalidatesTags: (result, error, {id}) => [{type: "Dispatches", id}],
    }),
    createDispatchNotifications: builder.mutation<DispatchNotificationBatchCreateResponse, DispatchNotificationBatchCreateRequest>({
      query: (req) => ({
        url: `/dispatch/notifications:batch`,
        method: "POST",
        body: req
      }),
      invalidatesTags: (result, error, req) => req.dispatchIds.map(id => ({type: "Dispatches" as const, id})),
    }),
    previewDispatchNotifications: builder.query<DispatchNotificationPreviewResponse, DispatchNotificationPreviewRequest>({
      query: (req) => ({
        url: `/dispatch/notifications:preview`,
        method: "POST",
        body: req
      }),
    }),
  }),
  overrideExisting: false
});

const usePrefetch = dispatchApi.usePrefetch

export {usePrefetch as useDispatchPrefetch}

export const {
  useGetDispatchQuery,
  useGetDispatchTrackingQuery,
  useGetDispatchCarryoversQuery,
  useGetDispatchCarryoverPolicyQuery,
  useGetDispatchNotificationsQuery,
  useSearchDispatchesQuery,
  useGetSplitDispatchesByDispatchQuery,
  useGetDispatchesByDayQuery,
  useCreateDispatchMutation,
  useUpdateDispatchMutation,
  usePatchDispatchMutation,
  useUpdateDispatchesMutation,
  useDeleteDispatchMutation,
  useCreateDispatchNotificationMutation,
  useCreateDispatchNotificationsMutation,
  useLazyPreviewDispatchNotificationsQuery,
} = dispatchApi;
